import { Checkbox, FormControlLabel } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { Fragment, useEffect, useLayoutEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import { assetDomain } from "../../../config/config";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import {
  updateExitDialog,
  updateSignUpProfileDialog,
} from "../../../redux/actions/dialog_action";
import { applyJob } from "../../../redux/actions/job_action";
import {
  updateCurrentSalary,
  updateOnboardingInfo,
} from "../../../redux/actions/user_action";
import { DirectSignUpFormUserParams } from "../../jobs/SecondaryVisitor/BaseDialog/types";
import FormTextField from "../../jobs/SecondaryVisitor/FormTextField/FormTextField";
import ResumeDropzone from "../../jobs/SecondaryVisitor/ResumeDropzone/ResumeDropzone";
import ActivelyAvailableDialog from "../../profile/ActivelyAvailableDialog/ActivelyAvailableDialog";
import SharedExitDialog from "../../shared/SharedExitDialog/SharedExitDialog";
import {
  BodyContactCaption,
  CheckIcon,
  CheckIdleIcon,
  CheckLabel,
  CheckedIcon,
  CurrentSalarySubText,
  CurrentSalaryText,
  DialogActionsStyled,
  DialogContentStyled,
  DialogContentWrapper,
  DialogStyled,
  DialogTitleContainer,
  DialogTitleContent,
  DialogTitleStyled,
  DividerStyled,
  EmptyContainer,
  HeaderCaption,
  LoadingButtonStyled,
  ResumeDropzoneText,
  ResumeMsgTypography,
  WhiteButton,
  useStyles,
} from "./styles";

export default function SignUpProfileDialog(props: any) {
  const dispatch: any = useDispatch();
  const router = useRouter();
  const [triggerSnackbarFunc] = useTriggerSnackbar();
  const { t } = useTranslation();
  const open = useSelector(
    (state: any) => state?.dialog?.showSignUpProfileDialog
  );
  const selectedJobId = useSelector((state: any) => state.jobs?.selectedJobId);
  const externalJobUrl = useSelector(
    (state: any) => state.jobs?.job?.externalJobUrl
  );

  const user = useSelector((state: any) => state?.user?.user);
  // const resume = useSelector((state: any) => state?.user?.resume);
  const mobile = useMediaQuery("(max-width:768px)");
  const [ref, inView, _] = useInView({ threshold: mobile ? 0.2 : 1 });
  const forms = ["Name", "Mobile Number", "Nationality"];

  const resumeExist =
    Object.keys(user).length > 0 &&
    user?.resume !== "" &&
    user?.state !== "no_resume";

  const [resumeUploaded, setResumeUploaded] = useState(resumeExist);
  const [resume, setResume] = useState(user?.resume ?? "");
  const [exist, setExist] = useState(false);
  const [formState, setFormState] = useState<"pending" | "loading" | "success">(
    "pending"
  );
  const [openForJob, setOpenForJob] = useState(true);
  const [showSwitchContainer, setShowSwitchContainer] = useState(false);
  const [userFormData, setUserFormData] = useState<DirectSignUpFormUserParams>({
    name: "",
    validName: true,
    phone: "",
    validPhone: true,
    nationality: "",
    validNationality: false,
    isFreshGrad: false,
    currentSalary: "",
  });
  const [isEmptyNameField, setIsEmptyNameField] = useState(false);
  const [isEmptyPhoneField, setIsEmptyPhoneField] = useState(false);
  const [isEmptyNationalityField, setIsEmptyNationalityField] = useState(false);
  const [invalidStyling, setInvalidStyling] = useState(false);

  const confettiGif = `${assetDomain}/gifs/confetti.gif`;
  const classes = useStyles();

  const userDetailsFulfilled =
    isEmpty(user?.name) ||
    (isEmpty(user?.mobileNumber) &&
      isEmpty(user?.nationality) &&
      isEmpty(resumeUploaded));

  const isSignUpProfileDialogOpen = sessionStorage.getItem(
    "triggerSignUpProfileDialog"
  );
  const originUrl = localStorage?.getItem("apply_origin");

  useEffect(() => {
    if (!isEmpty(user?.resume)) {
      setResumeUploaded(true);
      setResume(user?.resume);
    }
  }, [user]);

  useEffect(() => {
    if (!isEmpty(user)) {
      if (userDetailsFulfilled && isSignUpProfileDialogOpen && !originUrl) {
        setTimeout(() => {
          dispatch(updateSignUpProfileDialog(true));
        }, 1000);
      } else {
        dispatch(updateSignUpProfileDialog(false));
      }
    }
  }, [user, userDetailsFulfilled]);

  useLayoutEffect(() => {
    setShowSwitchContainer(inView ? false : true);
  }, [inView]);

  function validateEmptyFields() {
    if (!userFormData.name) {
      setIsEmptyNameField(true);
    } else {
      setIsEmptyNameField(false);
    }

    if (!userFormData.phone) {
      setIsEmptyPhoneField(true);
    } else {
      setIsEmptyPhoneField(false);
    }

    if (!userFormData.nationality) {
      setIsEmptyNationalityField(true);
    } else {
      setIsEmptyNationalityField(false);
    }
  }

  async function handleSubmit() {
    const invalidProfileDetails =
      !userFormData.name ||
      !userFormData.phone ||
      !userFormData.nationality ||
      !userFormData.validName ||
      !userFormData.validPhone ||
      !userFormData.validNationality;

    validateEmptyFields();

    if (invalidProfileDetails) {
      setInvalidStyling(true);
      return;
    }

    setFormState("loading");

    const userProfile = {
      name: userFormData.name,
      mobileNumber: userFormData.phone,
      nationality: userFormData.nationality,
      freshGrad: userFormData.isFreshGrad,
      email: user.email,
    };

    const formattedSalary = Number(
      String(userFormData.currentSalary).replace(/,/g, "")
    );

    const userSalary = {
      currentSalary: formattedSalary,
    };

    dispatch(updateOnboardingInfo(userProfile)).then(
      (updateProfileRes: any) => {
        if (updateProfileRes?.type === "UPDATE_ONBOARDING_INFO_FAILED") {
          triggerSnackbarFunc({
            snackbarMessage: t("sign_up_profile_dialog.error_generic"),
            severity: "error",
          });
        } else if (
          updateProfileRes?.type === "UPDATE_ONBOARDING_INFO_SUCCEED"
        ) {
          const { user } = updateProfileRes || {};
          // // identify user on posthog after is done onboarding
          // posthog.capture("update_user_properties", {
          //   distinctId: user?.id,

          //   $set: {
          //     uuid: user?.id,
          //     age: parseInt(user?.age ?? 0),
          //     gender: user?.gender,
          //     nationality: user?.nationality,
          //     is_actively_available: user?.activelyAvailable,
          //     has_resume: user?.resume ? true : false,
          //     current_location: user?.currentLocation,
          //     is_fresh_grad: user?.freshGrad,
          //     is_onboarding_completed: user?.onboardingComplete,
          //     signup_platform_device_os: user?.signupPlatformDeviceOs,
          //     track_ids: user?.trackIds,
          //   },
          // });
          const applySourceCE = "CE_onboarding_finish_click";
          const continueSignupFlow = () => {
            sendTrackingEvent({
              event: applySourceCE,
              origin: "onboarding",
            });
            // posthog.capture("onboarding_complete_profile_click", {
            //   origin: "resume_onboarding",
            //   step: "complete_signup",
            // });
            if (selectedJobId) {
              const applySourceNames =
                router.query?.origin === "job_list"
                  ? "jlp_apply_now"
                  : "jdp_apply_now";
              // Auto apply job
              dispatch(
                applyJob({
                  jobId: selectedJobId,
                  canCancel: false,
                  applySource: applySourceNames,
                })
              ).then((applyJobRes: any) => {
                if (applyJobRes.type === "APPLY_JOB_SUCCEED") {
                  if (externalJobUrl) {
                    window.open(externalJobUrl, "_blank");
                  }
                  setFormState("success");
                  sessionStorage.removeItem("triggerSignUpProfileDialog");
                  dispatch(updateSignUpProfileDialog(false));
                } else {
                  triggerSnackbarFunc({
                    snackbarMessage:
                      t("sign_up_profile_dialog.error_auto_apply"),
                    severity: "error",
                  });
                }
              });
            } else {
              setFormState("success");
              sessionStorage.removeItem("triggerSignUpProfileDialog");
              dispatch(updateSignUpProfileDialog(false));
              router.push("/jobs");
            }
          };

          if (formattedSalary > 0) {
            // Update user current salary
            dispatch(updateCurrentSalary(userSalary)).then((salaryRes: any) => {
              if (salaryRes?.type === "UPDATE_USER_PROFILE_FAILED") {
                triggerSnackbarFunc({
                  snackbarMessage: t(
                    "sign_up_profile_dialog.error_update_salary"
                ),
                severity: "error",
              });
              return;
            }

            continueSignupFlow();
            });

          return;
          }

          continueSignupFlow();
        } else {
          triggerSnackbarFunc({
            snackbarMessage: t("sign_up_profile_dialog.error_generic"),
            severity: "error",
          });
        }
      }
    );
  }

  function handleDoThisLaterClick() {
    // trigger exit dialog if user has filled in some info or uploaded resume
    if (
      !isEmpty(userFormData.name) ||
      !isEmpty(userFormData.phone) ||
      !isEmpty(userFormData.nationality) ||
      !isEmpty(resume)
    ) {
      dispatch(updateExitDialog(true));
    } else {
      dispatch(updateSignUpProfileDialog(false));
      sendTrackingEvent({
        event: "CE_onboarding_skip_click",
        origin: "onboarding",
      });
      sessionStorage.removeItem("triggerSignUpProfileDialog");
      router.push("/jobs");
    }
  }

  function handleCheckboxClick() {
    setUserFormData((prev) => ({
      ...prev,
      isFreshGrad: !prev.isFreshGrad,
    }));
  }

  return (
    userDetailsFulfilled && (
      <>
        <DialogStyled
          invalidProfileStyling={invalidStyling}
          open={open}
          success={formState === "success"}
        >
          <ActivelyAvailableDialog />
          {/* Header */}
          <DialogTitleContainer>
            <DialogTitleStyled>
              <DialogTitleContent>
                <CheckIcon />
                {t("sign_up_profile_dialog.title")}
              </DialogTitleContent>

              <HeaderCaption>
                {t("sign_up_profile_dialog.subtitle")}
              </HeaderCaption>
            </DialogTitleStyled>
          </DialogTitleContainer>

          {/* Content */}
          <DialogContentStyled className={classes.scrollBar}>
            <DialogContentWrapper>
              <Fragment>
                <BodyContactCaption>
                  {t("sign_up_profile_dialog.contact_details")}
                </BodyContactCaption>
                {forms.map((form, index) => {
                  return (
                    <FormTextField
                      key={`form-textfield-${index}`}
                      name={form}
                      userParams={userFormData}
                      setUserParams={setUserFormData}
                      exist={exist}
                      isEmptyNameField={isEmptyNameField}
                      setIsEmptyNameField={setIsEmptyNameField}
                      isEmptyPhoneField={isEmptyPhoneField}
                      setIsEmptyPhoneField={setIsEmptyPhoneField}
                      isEmptyNationalityField={isEmptyNationalityField}
                      setIsEmptyNationalityField={setIsEmptyNationalityField}
                    />
                  );
                })}
                <FormControlLabel
                  sx={{ height: "32px", marginBottom: "1rem" }}
                  control={
                    <Checkbox
                      onClick={handleCheckboxClick}
                      checkedIcon={<CheckedIcon />}
                      icon={<CheckIdleIcon />}
                    />
                  }
                  label={
                    <CheckLabel>
                      {t("sign_up_profile_dialog.fresh_grad")}
                    </CheckLabel>
                  }
                />
                <EmptyContainer>
                  <ResumeDropzoneText>
                    {t("sign_up_profile_dialog.add_resume")}
                  </ResumeDropzoneText>
                  <ResumeDropzone
                    ref={ref}
                    complete={resumeUploaded}
                    setComplete={setResumeUploaded}
                    resume={resume}
                    setResume={setResume}
                  />
                  <ResumeMsgTypography>
                    {t("sign_up_profile_dialog.resume_note")}
                  </ResumeMsgTypography>
                </EmptyContainer>
                <DividerStyled></DividerStyled>
                <CurrentSalaryText>
                  {t("sign_up_profile_dialog.salary_title")}
                </CurrentSalaryText>
                <CurrentSalarySubText>
                  <Trans
                    i18nKey="sign_up_profile_dialog.salary_subtitle"
                    components={{ b: <b /> }}
                  />
                </CurrentSalarySubText>
                <CurrentSalarySubText>
                  <Trans
                    i18nKey="sign_up_profile_dialog.salary_privacy"
                    components={{ b: <b /> }}
                  />
                </CurrentSalarySubText>
                <FormTextField
                  name="Current Salary"
                  userParams={userFormData}
                  setUserParams={setUserFormData}
                  exist={exist}
                />
              </Fragment>
            </DialogContentWrapper>
          </DialogContentStyled>

          {/* Action */}
          <DialogActionsStyled>
            <Fragment>
              <WhiteButton onClick={handleDoThisLaterClick}>
                {t("sign_up_profile_dialog.do_later")}
              </WhiteButton>
              <LoadingButtonStyled
                loading={formState === "loading"}
                onClick={handleSubmit}
              >
                {formState === "loading"
                  ? ""
                  : t("sign_up_profile_dialog.continue")}
              </LoadingButtonStyled>
            </Fragment>
          </DialogActionsStyled>
        </DialogStyled>
        {/* for showing exit confirmation if user has resume/info but decides to drop off */}
        <SharedExitDialog />
      </>
    )
  );
}
